import { Alert, Badge, Button, Card, Group, Modal, SimpleGrid, Stack, Text, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { Link } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { type CashPair, MoneyPair, OverShortPair } from '../../components/CashFigures';
import { useLocationId } from '../../hooks/useSeedLocation';
import { formatDateTime } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { CountSheet, useCountSheet } from './CountSheet';

/**
 * THE REGISTERS WITH NOBODY BEHIND THEM — the half of his sentence that had no screen and no door.
 *
 * > "I should be able to pull the drawer at any time **whether we are open or closed** and the
 * > system should report how much is expected in the drawer."
 *
 * A drawer on a shift is pulled from that shift's own page. A drawer with no shift open on it —
 * which is every register in this bar every hour it is closed, and every register in this system until the
 * Phase 2 terminal exists — could not be counted at all. The office refused it: *"a cash drawer is
 * counted through the shift it belongs to, not on its own."* That is a true sentence about a drawer
 * with a shift and it has nothing to say about a Sunday afternoon.
 *
 * ## The three things a card here can say, and why the third is not a nought
 *
 * **What the last shift left in it.** Frozen at that shift's close, and it does not move: every door
 * into a drawer is refused unless a session is open on it, so nothing can have touched the money
 * since. On an ordinary night it is the standard float, because the reset put the float back and the
 * pouch went into the safe.
 *
 * **Trading right now**, in which case this is the wrong screen and the card says which is the right
 * one. That register has a live expectation and a lever to move it; neither exists here.
 *
 * **Nothing at all**, on a drawer no shift has ever been opened on. Not nought — nought would claim
 * the register is empty, and the float may be sitting in it, put there by hand the way the change in the
 * float box is. So there is nothing to measure the count against, the count is recorded as exactly
 * that, and the screen does not print a green verdict about money nobody has a figure for.
 *
 * ## There is no "move the drawer" here, and that is the model rather than an omission
 *
 * Mid-shift he may move the drawer onto what he counted, because the rest of the shift is measured
 * against that figure. Between shifts nothing is — the next shift's expectation comes from its own
 * opening count — so a button here would be a lever with nothing on the other end of it.
 *
 * What the count is FOR is that it exists, dated and with his name on it, **before** tomorrow's float
 * is counted in. An opening count is measured against the standard float, so a register that lost five
 * hundred lempiras on a Sunday hands tomorrow's bartender a light opening variance; the count is
 * what puts the reason beside it instead of on them.
 */

interface DrawerCount {
  cashCountId: string;
  countedAt: string;
  counted: CashPair;
  expected: CashPair | null;
  variance: CashPair | null;
  checkedByName: string | null;
  note: string | null;
}

export interface Drawer {
  cashLocationId: string;
  name: string;
  registerNames: string[];
  tradingSessionId: string | null;
  expected: CashPair | null;
  expectedAsOf: string | null;
  counts: DrawerCount[];
}

interface CountResult {
  expected: CashPair | null;
  counted: CashPair;
  variance: CashPair | null;
  allThere: boolean;
}

export const DRAWERS_KEY = listKey('/admin/v1/cash/drawers');

/**
 * Every cash drawer and whether a shift is open on it — asked once, read by two screens.
 *
 * The register screen needs the same answer this panel does, for a different reason: the standard
 * float it lets the owner edit is not configuration that only bites tomorrow. A hand-written second
 * query under the same cache key would race this one and serve whichever URL happened to fire first,
 * which is the drift `api/queryKeys.ts` exists to stop, so the query lives here and both callers
 * share it.
 */
export function useDrawers() {
  const locationId = useLocationId();
  const where = locationId ? `?locationId=${encodeURIComponent(locationId)}` : '';
  return useQuery({
    queryKey: DRAWERS_KEY,
    queryFn: () => rawApi<Drawer[]>('GET', `/admin/v1/cash/drawers${where}`),
  });
}

export function PullADrawer() {
  const [counting, setCounting] = useState<Drawer | null>(null);

  const drawers = useDrawers();

  const registers = drawers.data ?? [];
  /*
   * Every count taken on a drawer on its own, across all of them, newest first. On the page rather
   * than behind a card, because a count nobody can read afterwards is evidence nobody has — and
   * because the row that matters is the one from the afternoon before a bartender opened light.
   */
  const counts = registers
    .flatMap((drawer) => drawer.counts.map((count) => ({ ...count, drawerName: drawer.name })))
    .sort((a, b) => b.countedAt.localeCompare(a.countedAt));

  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
        {registers.map((drawer) => (
          <Card key={drawer.cashLocationId} withBorder>
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <div>
                <Text size="xs" c="dimmed">
                  {drawer.name}
                </Text>
                {/* Two registers counting into one drawer is a shortage that can never be put to a
                    person, and this is where it is visible. One is the ordinary case and needs no
                    label. */}
                {drawer.registerNames.length > 1 && (
                  <Text size="xs" c="orange">
                    {drawer.registerNames.join(' and ')} both count into this one
                  </Text>
                )}
              </div>
              {drawer.tradingSessionId ? (
                <Badge size="xs" color="teal" variant="light">
                  Trading
                </Badge>
              ) : (
                <Button size="compact-xs" variant="subtle" onClick={() => setCounting(drawer)}>
                  Count it
                </Button>
              )}
            </Group>

            {drawer.tradingSessionId ? (
              <>
                <Text size="sm" mt={6}>
                  A shift is open on this register.
                </Text>
                <Button
                  size="compact-xs"
                  variant="light"
                  mt={6}
                  component={Link}
                  to={`/register/drawers/${drawer.tradingSessionId}`}
                >
                  Pull it through the shift
                </Button>
                <Text size="xs" c="dimmed" mt={4}>
                  What it should be holding is moving with every sale, and it can be moved onto what you count
                </Text>
              </>
            ) : drawer.expected ? (
              <>
                <MoneyPair pair={drawer.expected} fw={700} />
                <Text size="xs" c="dimmed" mt={4}>
                  {drawer.expectedAsOf
                    ? `Left in it when the last shift was counted off, ${formatDateTime(drawer.expectedAsOf)}`
                    : 'Left in it by the last shift'}
                </Text>
              </>
            ) : (
              /*
                No figure, and no nought either. A card printing L 0.00 here would be telling him the
                register is empty, and this is the state every drawer in this bar is in until a terminal
                opens a shift on one — the float may well be in it, put there by hand.
              */
              <>
                <Text size="sm" c="dimmed" mt={6}>
                  Nothing recorded yet
                </Text>
                <Text size="xs" c="dimmed" mt={4}>
                  No shift has ever been opened on this register, so there is nothing to measure a count
                  against. Count it anyway and what you found is on record
                </Text>
              </>
            )}
          </Card>
        ))}
        {registers.length === 0 && !drawers.isLoading && (
          <Text size="sm" c="dimmed">
            No cash drawer has been set up yet.
          </Text>
        )}
      </SimpleGrid>

      {counts.length > 0 && (
        <>
          <Text size="sm" fw={600} mt="sm">
            Counts taken with the register shut
          </Text>
          <DataTable
            withTableBorder
            minHeight={80}
            records={counts}
            idAccessor="cashCountId"
            noRecordsText="No drawer has been counted with the bar closed."
            columns={[
              { accessor: 'countedAt', title: 'When', render: (row) => formatDateTime(row.countedAt) },
              { accessor: 'drawerName', title: 'Drawer' },
              {
                accessor: 'counted',
                title: 'You counted',
                render: (row) => <MoneyPair pair={row.counted} />,
              },
              {
                accessor: 'expected',
                title: 'Should have been',
                // A count measured against nothing is not a count measured against nought, and this
                // column is where the difference is either said or lost.
                render: (row) =>
                  row.expected ? (
                    <MoneyPair pair={row.expected} />
                  ) : (
                    <Text size="sm" c="dimmed">
                      Nothing recorded
                    </Text>
                  ),
              },
              {
                accessor: 'variance',
                title: 'Difference',
                render: (row) =>
                  row.variance ? (
                    <OverShortPair pair={row.variance} />
                  ) : (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  ),
              },
              { accessor: 'checkedByName', title: 'Counted by', render: (row) => row.checkedByName ?? '—' },
              { accessor: 'note', title: 'Note', render: (row) => row.note ?? '' },
            ]}
          />
        </>
      )}

      <CountTheDrawer drawer={counting} onClose={() => setCounting(null)} />
    </Stack>
  );
}

/**
 * The count itself, in a modal, with the expectation withheld until he submits.
 *
 * He is genuinely allowed to see what should be in there — the blind-count rule protects the person
 * the variance would be attributed to, and that is the bartender rather than him. But a figure on
 * screen while somebody counts is a figure the counting drifts towards, so it arrives with the
 * answer. The card behind this modal carries it for the same reason it is not repeated inside.
 */
function CountTheDrawer({ drawer, onClose }: { drawer: Drawer | null; onClose: () => void }) {
  const locationId = useLocationId();
  const where = locationId ? `?locationId=${encodeURIComponent(locationId)}` : '';
  const queryClient = useQueryClient();
  const sheet = useCountSheet();
  const [note, setNote] = useState('');
  const [result, setResult] = useState<CountResult | null>(null);

  const count = useMutation({
    mutationFn: () =>
      rawApi<CountResult>('POST', `/admin/v1/cash/drawers/${drawer?.cashLocationId}/audit${where}`, {
        denominationLines: sheet.lines(),
        note: note || null,
      }),
    onSuccess: (answer) => {
      setResult(answer);
      notifyDone(
        'Count recorded',
        answer.expected === null
          ? 'What you found is on record. There was nothing to measure it against.'
          : answer.allThere
            ? 'The money is all there.'
            : 'The difference is recorded against this drawer, with your name and the time.',
      );
      void queryClient.invalidateQueries({ queryKey: DRAWERS_KEY });
    },
    onError: (error) => notifyError(error, 'record what you counted'),
  });

  const close = () => {
    sheet.clear();
    setNote('');
    setResult(null);
    onClose();
  };

  return (
    <Modal opened={drawer !== null} onClose={close} title={`Count the ${drawer?.name ?? 'drawer'}`} size="lg">
      {result ? (
        <Stack gap="sm">
          <Group gap="lg">
            <div>
              <Text size="xs" c="dimmed">
                Should have been there
              </Text>
              {result.expected ? (
                <MoneyPair pair={result.expected} fw={700} />
              ) : (
                <Text size="sm" c="dimmed">
                  Nothing recorded
                </Text>
              )}
            </div>
            <div>
              <Text size="xs" c="dimmed">
                You counted
              </Text>
              <MoneyPair pair={result.counted} fw={700} />
            </div>
            <div>
              <Text size="xs" c="dimmed">
                Difference
              </Text>
              {result.variance ? (
                <OverShortPair pair={result.variance} />
              ) : (
                <Text size="sm" c="dimmed">
                  —
                </Text>
              )}
            </div>
          </Group>
          {/*
            THE VERDICT, AND THE STATE IT MUST NOT BE GIVEN IN — the same rule as the drop panel.

            The server reads a count with no expectation behind it as clear, because it is not
            claiming a discrepancy. That is the right answer to the server's question and the wrong
            words for this screen: "all there" against a register nobody has a figure for is a green
            sentence about money nobody has checked. So the wording keys off whether there was
            anything to be all there WITH.
          */}
          {result.expected === null ? (
            <Alert color="gray" variant="light">
              There was nothing to measure this against — no shift has been opened on this register, so
              nothing has ever been recorded going into it. What you found is on record with your name and the
              time, and the first shift opened on it will count its float in against the standard float.
            </Alert>
          ) : result.allThere ? (
            <Alert color="green" variant="light">
              The money is all there. It is what the last shift left in the register, to the centavo.
            </Alert>
          ) : (
            <Alert color="orange" variant="light">
              <Text size="sm" fw={600} mb={4}>
                Recorded, not written off.
              </Text>
              <Text size="sm">
                There is nothing to move: the next shift opened on this register counts its own float in and
                starts from that. What this count does is sit here, dated, so an opening float that comes up
                light tomorrow has the reason beside it instead of landing on whoever opened the register.
              </Text>
            </Alert>
          )}
          <Group justify="flex-end">
            <Button onClick={close}>Done</Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Count what is actually in the register. What it should be holding is shown once you have
            submitted, so the figure on the screen cannot lead the counting.
          </Text>
          <CountSheet
            notes={sheet.notes}
            loading={sheet.loading}
            /* A failed denominations read leaves the sheet rowless and the submit inert — no wrong
               count can post — but without this the modal blames the bar's configuration for it. */
            error={sheet.error}
            sheet={sheet.sheet}
            onChange={(key, value) => sheet.setSheet({ ...sheet.sheet, [key]: value })}
          />
          <Textarea
            label="Note"
            description="Anything worth remembering about this count."
            autosize
            minRows={1}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          <Group justify="space-between">
            <Group gap={6}>
              <Text size="sm" c="dimmed">
                On the sheet
              </Text>
              <MoneyPair pair={sheet.running} fw={700} />
            </Group>
            <Button
              disabled={!sheet.anythingTyped || count.isPending}
              loading={count.isPending}
              onClick={() => count.mutate()}
            >
              Record what I counted
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
